
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  useTheme
} from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  CheckCircle as CheckIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Diamond as DiamondIcon
} from '@mui/icons-material'
import { motion } from 'framer-motion'

// Icon mapping for plans
const iconMap = {
  'Free': TrendingUpIcon,
  'Lite': StarIcon,
  'Standard': CheckIcon,
  'Pro': DiamondIcon
}

// Note: All plan properties now come directly from database

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1
    }
  }
}

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  },
  hover: {
    y: -8,
    scale: 1.02,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  }
}

const Membership = () => {
  const theme = useTheme()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [isValidating, setIsValidating] = useState(true)
  const [isValidToken, setIsValidToken] = useState(false)
  const [userInfo, setUserInfo] = useState(null)
  const [error, setError] = useState(null)
  const [plans, setPlans] = useState([])
  const [plansLoading, setPlansLoading] = useState(true)
  
  // Fetch plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/plans`)
        if (response.ok) {
          const data = await response.json()
          let plansArray = [];
          if (data.plans && Array.isArray(data.plans)) {
            plansArray = data.plans;
          } else if (data.data && typeof data.data === 'object') {
            plansArray = Object.values(data.data);
          } else if (Array.isArray(data)) {
            // Format: [...] (direct array)
            plansArray = data;
          }
          
          // Transform plans to include icon components
          const transformedPlans = plansArray.map(plan => {
            const planName = plan.name || plan.displayName || 'Unknown Plan';
            const planId = plan.id || planName.toLowerCase();
            
            return {
              ...plan,
              id: planId,
              name: planName,
              price: plan.price || 0,
              maxReminders: plan.maxReminders || 0,
              features: plan.features || [],
              color: plan.color || '#6366f1',
              icon: iconMap[plan.icon] || TrendingUpIcon,
              popular: plan.popular || false,
              description: plan.description || `${planName} plan with ${plan.maxReminders || 0} reminders`,
              originalPrice: (plan.price || 0) * 2, // For display purposes
              period: 'month',
              reminders: plan.maxReminders || 0,
              messagesPerReminder: 10,
              totalMessages: (plan.maxReminders || 0) * 10
            };
          })

          console.log('Transformed plans:', transformedPlans);
          setPlans(transformedPlans)
        } else {
          console.error('Failed to fetch plans')
        }
      } catch (error) {
        console.error('Error fetching plans:', error)
      } finally {
        setPlansLoading(false)
      }
    }

    fetchPlans()
  }, [])
  
  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No token provided')
        setIsValidating(false)
        return
      }

      try {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/validate-token/${token}`)
        const data = await response.json()
        if (data.success) {
          setIsValidToken(true)
          setUserInfo(data.data)
        } else {
          setError(data.message || 'Invalid token')
          setIsValidToken(false)
        }
      } catch (err) {
        console.error('Token validation error:', err)
        setError('Failed to validate token')
        setIsValidToken(false)
      } finally {
        setIsValidating(false)
      }
    }

    validateToken()
  }, [token])
  
  const handlePlanSelect = (planId) => {
    const phoneNumber = userInfo?.phoneNumber || userInfo?.phone || ''
    console.log('Full URL will be:', `/payment?plan=${planId}&token=${token}&phone=${encodeURIComponent(phoneNumber)}`)
    
    window.location.href = `/payment?plan=${planId}&token=${token}`
  }

  // Show loading state while validating token
  if (isValidating || plansLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h4" gutterBottom>
              {isValidating ? 'Validating Access...' : 'Loading Plans...'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isValidating ? 'Please wait while we verify your access token.' : 'Please wait while we load available plans.'}
            </Typography>
          </Box>
        </Container>
      </Box>
    )
  }

  // Show 404 error if token is invalid
  if (!isValidToken || error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h1" component="h1" gutterBottom sx={{ fontSize: '6rem', fontWeight: 700, color: theme.palette.error.main }}>
              404
            </Typography>
            <Typography variant="h4" gutterBottom>
              User Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {error || 'The provided token is invalid or has expired. Please check your access link and try again.'}
            </Typography>
          </Box>
        </Container>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        py: 2
      }}
    >
      <Container maxWidth="xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <Box textAlign="center" mb={6}>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                Choose Your Plan
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}
              >
                {userInfo ? `Welcome back, ${userInfo.name || userInfo.phoneNumber}! ` : ''}Unlock the full potential of your WhatsApp reminder bot with our flexible plans
              </Typography>
              
              {/* Current Plan Display */}
              {userInfo && userInfo.subscription && (
                <Box mt={3}>
                  <Typography
                    variant="h6"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      border: `2px solid ${theme.palette.success.main}`,
                      color: theme.palette.success.main,
                      fontWeight: 600
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 20 }} />
                    Currently using: {userInfo.subscription.planType} Plan
                  </Typography>
                </Box>
              )}
              
            </motion.div>
          </Box>

          {/* Plans Grid */}
          <Grid container spacing={2} justifyContent="center">
            {plans.map((plan) => {
              const IconComponent = plan.icon
              const isPopular = plan.popular
              const isCurrentPlan = userInfo && userInfo.subscription && 
                userInfo.subscription.planType.toLowerCase() === plan.id

              return (
                <Grid item xs={12} sm={6} md={3} key={plan.id}>
                  <motion.div
                    variants={cardVariants}
                    whileHover="hover"
                    style={{ height: '100%' }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        position: 'relative',
                        border: isCurrentPlan
                          ? `2px solid ${theme.palette.success.main}`
                          : isPopular
                          ? `2px solid ${plan.color}`
                          : `1px solid ${theme.palette.divider}`,
                        background: isPopular
                          ? `linear-gradient(135deg, ${plan.color}08, ${plan.color}15)`
                          : theme.palette.background.paper,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: theme.shadows[8],
                          borderColor: isCurrentPlan ? theme.palette.success.main : plan.color
                        }
                      }}
                    >

                      {/* Popular Badge */}
                      {isPopular && !isCurrentPlan && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -0.5,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 1
                          }}
                        >
                          <Chip
                            label="Most Popular"
                            size="small"
                            sx={{
                              fontWeight: 600,
                              px: 5,
                              py: 0.5,
                              backgroundColor: plan.color,
                              color: 'white',
                              '&:hover': {
                                backgroundColor: plan.color,
                                opacity: 0.9
                              }
                            }}
                          />
                        </Box>
                      )}

                      <CardContent sx={{ pt: isPopular ? 4 : 3, pb: 2 }}>
                        {/* Plan Header */}
                        <Box textAlign="center" mb={3}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 64,
                              height: 64,
                              borderRadius: '50%',
                              background: `linear-gradient(135deg, ${plan.color}, ${plan.color}dd)`,
                              color: 'white',
                              mb: 2
                            }}
                          >
                            <IconComponent sx={{ fontSize: 32 }} />
                          </Box>

                          <Typography variant="h4" component="h3" gutterBottom fontWeight={600}>
                            {plan.name}
                          </Typography>

                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="h3"
                              component="span"
                              sx={{
                                fontWeight: 700,
                                color: plan.color
                              }}
                            >
                              ${plan.price}
                            </Typography>
                            <Typography
                              variant="h6"
                              component="span"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              /{plan.period}
                            </Typography>
                          </Box>

                          {plan.originalPrice > plan.price && (
                            <Typography
                              variant="body2"
                              sx={{
                                textDecoration: 'line-through',
                                color: 'text.disabled',
                                mb: 1
                              }}
                            >
                              ${plan.originalPrice}/{plan.period}
                            </Typography>
                          )}
                        </Box>

                        {/* Plan Stats */}
                        <Box
                          sx={{
                            background: theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.03)',
                            borderRadius: 2,
                            p: 2,
                            mb: 3
                          }}
                        >
                          <Grid container spacing={2} textAlign="center">
                            <Grid item xs={4}>
                              <Typography variant="h6" fontWeight={600} color={plan.color}>
                                {plan.reminders}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Reminders
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="h6" fontWeight={600} color={plan.color}>
                                {plan.messagesPerReminder}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Per Reminder
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="h6" fontWeight={600} color={plan.color}>
                                {plan.totalMessages}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Total Messages
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>

                        {/* Features */}
                        <Box mb={3}>
                          {plan.features.map((feature, featureIndex) => (
                            <Box
                              key={featureIndex}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1
                              }}
                            >
                              <CheckIcon
                                sx={{
                                  color: plan.color,
                                  fontSize: 20,
                                  mr: 1.5
                                }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                {feature}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </CardContent>

                      <CardActions sx={{ p: 3, pt: 0 }}>
                        <Button
                          fullWidth
                          variant={isCurrentPlan ? "outlined" : isPopular ? "contained" : "outlined"}
                          size="large"
                          onClick={() => handlePlanSelect(plan.id)}
                          disabled={isCurrentPlan}
                          sx={{
                            py: 1.5,
                            fontWeight: 600,
                            fontSize: '1.1rem',
                            background: isCurrentPlan
                              ? theme.palette.success.light
                              : isPopular
                              ? `linear-gradient(135deg, ${plan.color}, ${plan.color}dd)`
                              : 'transparent',
                            borderColor: isCurrentPlan ? theme.palette.success.main : plan.color,
                            color: isCurrentPlan ? 'white' : isPopular ? 'white' : plan.color,
                            '&.MuiButton-root': {
                              color: isCurrentPlan ? 'white' : isPopular ? 'white' : plan.color,
                            },
                            '&:hover': {
                              background: isCurrentPlan
                                ? theme.palette.success.light
                                : isPopular
                                ? `linear-gradient(135deg, ${plan.color}dd, ${plan.color})`
                                : `${plan.color}08`,
                              borderColor: isCurrentPlan ? theme.palette.success.main : plan.color,
                              transform: isCurrentPlan ? 'none' : 'translateY(-2px)',
                              boxShadow: isCurrentPlan ? 'none' : `0 8px 25px ${plan.color}40`
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {isCurrentPlan ? 'Active Plan' : isPopular ? 'Get Started' : 'Choose Plan'}
                        </Button>
                      </CardActions>
                    </Card>
                  </motion.div>
                </Grid>
              )
            })}
          </Grid>

          {/* Footer */}
          <Box textAlign="center" mt={6}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                All plans include a 7-day free trial. Cancel anytime.
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Need help? Contact our support team
              </Typography>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
    </Box>
  )
}

export default Membership
